import React, { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { StickyNote, Plus, Search, Pin, PinOff, Trash2, ImagePlus, Bold, List, CheckSquare, X, Loader2, Palette } from "lucide-react";
import { api, fileUrl } from "../lib/api";
import { useCompany } from "../context/CompanyContext";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";

const COLORS = {
  default: { bg: "var(--card)", ring: "var(--border)" },
  red: { bg: "#3b1f24", ring: "#7f2d3a", light: "#fde8ea" },
  orange: { bg: "#3a2a1c", ring: "#7c4a22", light: "#fdecd8" },
  yellow: { bg: "#3a371c", ring: "#7c7022", light: "#fbf3d0" },
  green: { bg: "#1d3326", ring: "#236141", light: "#d9f2e3" },
  teal: { bg: "#173434", ring: "#1f6363", light: "#d2f0ef" },
  blue: { bg: "#1c2c44", ring: "#2563eb", light: "#dbe7fb" },
  purple: { bg: "#2c2240", ring: "#6d4ab5", light: "#e9def8" },
  pink: { bg: "#3a2030", ring: "#7c3060", light: "#fbdcec" },
};
const PALETTE = Object.keys(COLORS);

function cardStyle(color) {
  const c = COLORS[color] || COLORS.default;
  const isLight = typeof document !== "undefined" && document.documentElement.classList.contains("light");
  if (color === "default") return { background: "hsl(var(--card))", borderColor: "hsl(var(--border))" };
  return { background: isLight ? c.light : c.bg, borderColor: isLight ? c.ring + "55" : c.ring + "88" };
}

export default function NotesPage() {
  const { selectedId } = useCompany();
  const [notes, setNotes] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/notes"); setNotes(data); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = q ? notes.filter((n) => ((n.title || "") + " " + (n.content || "")).toLowerCase().includes(q.toLowerCase())) : notes;
  const pinned = filtered.filter((n) => n.pinned);
  const others = filtered.filter((n) => !n.pinned);

  const save = async (note) => {
    const payload = { title: note.title, content: note.content, color: note.color, pinned: note.pinned, images: note.images, company_id: (selectedId && selectedId !== "all") ? selectedId : null };
    if (note.id) { const { data } = await api.patch(`/notes/${note.id}`, payload); setNotes((p) => p.map((x) => (x.id === data.id ? data : x))); }
    else { const { data } = await api.post("/notes", payload); setNotes((p) => [data, ...p]); }
  };
  const togglePin = async (n) => { const { data } = await api.patch(`/notes/${n.id}`, { pinned: !n.pinned }); setNotes((p) => p.map((x) => (x.id === data.id ? data : x))); };
  const remove = async (id) => { await api.delete(`/notes/${id}`); setNotes((p) => p.filter((x) => x.id !== id)); setEditing(null); toast.success("Note supprimée"); };

  return (
    <div className="h-full overflow-y-auto bg-background">
      <div className="sticky top-0 z-10 bg-card/70 backdrop-blur-xl px-4 lg:px-8 py-3 flex items-center gap-3 border-b border-white/[0.06]">
        <div className="flex items-center gap-2"><StickyNote size={18} className="text-klein" /><span className="font-cabinet font-bold text-lg hidden sm:inline">Notes</span></div>
        <div className="flex items-center gap-2 bg-background border border-white/10 rounded-full px-4 h-9 flex-1 max-w-md">
          <Search size={15} className="text-zinc-500" />
          <input data-testid="notes-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher dans les notes…" className="flex-1 bg-transparent text-sm focus:outline-none" />
        </div>
        <button data-testid="notes-new" onClick={() => setEditing({ title: "", content: "", color: "default", pinned: false, images: [] })} className="flex items-center gap-1.5 px-4 h-9 rounded-full bg-klein text-zinc-950 text-sm font-semibold hover:-translate-y-px hover:shadow-[0_4px_14px_rgba(37,99,235,0.4)] transition-all"><Plus size={16} /> Note</button>
      </div>

      <div className="px-4 lg:px-8 py-6">
        {loading ? <div className="py-20 flex justify-center text-zinc-400"><Loader2 className="animate-spin" /></div>
          : filtered.length === 0 ? (
            <div className="py-24 flex flex-col items-center text-zinc-400">
              <StickyNote size={56} strokeWidth={1} />
              <p className="mt-4 text-sm">Aucune note. Créez votre premier compte rendu.</p>
            </div>
          ) : (
            <>
              {pinned.length > 0 && <>
                <p className="font-mono-label text-[10px] uppercase tracking-widest text-zinc-500 mb-3 flex items-center gap-1.5"><Pin size={11} /> Épinglées</p>
                <Masonry notes={pinned} onOpen={setEditing} onPin={togglePin} />
                {others.length > 0 && <p className="font-mono-label text-[10px] uppercase tracking-widest text-zinc-500 mb-3 mt-8">Autres</p>}
              </>}
              <Masonry notes={others} onOpen={setEditing} onPin={togglePin} />
            </>
          )}
      </div>

      {editing && <NoteEditor note={editing} onClose={() => setEditing(null)} onSave={save} onDelete={remove} />}
    </div>
  );
}

function Masonry({ notes, onOpen, onPin }) {
  return (
    <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4 [column-fill:_balance]">
      {notes.map((n) => {
        const firstImg = (n.images || [])[0];
        return (
          <button key={n.id} data-testid={`note-card-${n.id}`} onClick={() => onOpen(n)}
            className="group mb-4 w-full break-inside-avoid text-left rounded-2xl border p-4 hover:shadow-lg transition-shadow relative overflow-hidden"
            style={cardStyle(n.color)}>
            <span onClick={(e) => { e.stopPropagation(); onPin(n); }} className="absolute top-2.5 right-2.5 p-1.5 rounded-full opacity-0 group-hover:opacity-100 hover:bg-black/10 transition-opacity" title={n.pinned ? "Désépingler" : "Épingler"}>
              {n.pinned ? <Pin size={14} className="text-klein fill-klein" /> : <Pin size={14} className="text-zinc-400" />}
            </span>
            {firstImg && <img src={firstImg.url} alt="" className="w-full max-h-44 object-cover rounded-lg mb-3 border border-black/5" />}
            {n.title && <h3 className="font-cabinet font-bold text-base mb-1.5 pr-6 leading-snug">{n.title}</h3>}
            <div className="fd-note-preview text-sm text-zinc-500 leading-relaxed line-clamp-[10]" dangerouslySetInnerHTML={{ __html: stripImgs(n.content) }} />
            <div className="mt-3 text-[10px] font-mono-label text-zinc-400">{new Date(n.updated_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</div>
          </button>
        );
      })}
    </div>
  );
}

function stripImgs(html) { return (html || "").replace(/<img[^>]*>/g, ""); }

function NoteEditor({ note, onClose, onSave, onDelete }) {
  const ref = useRef(null);
  const fileRef = useRef(null);
  const [title, setTitle] = useState(note.title || "");
  const [color, setColor] = useState(note.color || "default");
  const [pinned, setPinned] = useState(!!note.pinned);
  const [images, setImages] = useState(note.images || []);
  const [showColors, setShowColors] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => { if (ref.current) ref.current.innerHTML = note.content || ""; /* eslint-disable-next-line */ }, []);

  const exec = (cmd, v = null) => { ref.current?.focus(); document.execCommand(cmd, false, v); };
  const insertPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = fileUrl(data.id);
      setImages((p) => [...p, { url }]);
      ref.current?.focus();
      document.execCommand("insertHTML", false, `<img src="${url}" style="max-width:100%;border-radius:12px;margin:8px 0;display:block;" />`);
    } catch { toast.error("Échec de l'envoi de la photo"); }
    finally { setUploading(false); }
  };

  const collectImages = () => {
    const html = ref.current?.innerHTML || "";
    const urls = [...html.matchAll(/<img[^>]+src=\"([^\"]+)\"/g)].map((m) => ({ url: m[1] }));
    return urls;
  };

  const handleSave = async () => {
    const content = ref.current?.innerHTML || "";
    if (!title && !ref.current?.innerText?.trim() && collectImages().length === 0) { onClose(); return; }
    await onSave({ ...note, title, content, color, pinned, images: collectImages() });
    toast.success(note.id ? "Note enregistrée" : "Note créée");
    onClose();
  };

  const isLight = typeof document !== "undefined" && document.documentElement.classList.contains("light");
  const bg = color === "default" ? "hsl(var(--card))" : (isLight ? COLORS[color].light : COLORS[color].bg);

  return (
    <Dialog open onOpenChange={(o) => { if (!o) handleSave(); }}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden rounded-2xl" style={{ background: bg }} data-testid="note-editor">
        <DialogTitle className="sr-only">Éditeur de note</DialogTitle>
        <div className="flex items-center gap-2 px-4 pt-4">
          <input data-testid="note-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Titre du compte rendu…" className="flex-1 bg-transparent font-cabinet font-bold text-xl focus:outline-none placeholder:text-zinc-400" />
          <button data-testid="note-pin" onClick={() => setPinned((p) => !p)} className="p-2 rounded-full hover:bg-black/10" title="Épingler">{pinned ? <Pin size={18} className="text-klein fill-klein" /> : <PinOff size={18} className="text-zinc-400" />}</button>
        </div>
        <div ref={ref} data-testid="note-content" contentEditable suppressContentEditableWarning data-placeholder="Prenez des notes, insérez des photos…"
          className="fd-editor px-4 py-3 min-h-[280px] max-h-[55vh] overflow-y-auto outline-none text-[15px] leading-relaxed" />
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => insertPhoto(e.target.files?.[0])} />
        <div className="flex items-center gap-1 px-3 py-2.5 border-t border-black/10">
          <button onClick={() => exec("bold")} className="p-2 rounded-lg hover:bg-black/10 text-zinc-400" title="Gras"><Bold size={16} /></button>
          <button onClick={() => exec("insertUnorderedList")} className="p-2 rounded-lg hover:bg-black/10 text-zinc-400" title="Liste"><List size={16} /></button>
          <button onClick={() => exec("insertHTML", "<div>☐ </div>")} className="p-2 rounded-lg hover:bg-black/10 text-zinc-400" title="Case à cocher"><CheckSquare size={16} /></button>
          <button data-testid="note-add-photo" onClick={() => fileRef.current?.click()} disabled={uploading} className="p-2 rounded-lg hover:bg-black/10 text-klein" title="Insérer une photo">{uploading ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />}</button>
          <div className="relative">
            <button onClick={() => setShowColors((s) => !s)} className="p-2 rounded-lg hover:bg-black/10 text-zinc-400" title="Couleur"><Palette size={16} /></button>
            {showColors && (
              <div className="absolute bottom-full left-0 mb-2 z-10 bg-card border border-white/10 rounded-xl p-2 grid grid-cols-5 gap-1.5 shadow-2xl">
                {PALETTE.map((c) => (
                  <button key={c} data-testid={`note-color-${c}`} onClick={() => { setColor(c); setShowColors(false); }} className={`w-6 h-6 rounded-full border-2 ${color === c ? "border-klein" : "border-white/20"}`} style={{ background: c === "default" ? "hsl(var(--muted))" : (COLORS[c].light || COLORS[c].ring) }} />
                ))}
              </div>
            )}
          </div>
          <div className="ml-auto flex items-center gap-1.5">
            {note.id && <button data-testid="note-delete" onClick={() => onDelete(note.id)} className="p-2 rounded-lg hover:bg-signal/10 text-zinc-400 hover:text-signal" title="Supprimer"><Trash2 size={16} /></button>}
            <button data-testid="note-save" onClick={handleSave} className="px-4 py-1.5 rounded-full bg-klein text-zinc-950 text-sm font-semibold hover:brightness-110">Enregistrer</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
